import React, { useState, useEffect } from 'react';
import { ShippingDisclaimer } from '../ShippingDisclaimer';

interface Quote {
  quoteId: string;
  carrierName: string;
  carrierCode: string;
  serviceDescription: string;
  totalAmount: number;
  // No baseAmount / markupPercent / markupAmount — the API strips carrier cost
  // for dealers on purpose. Do not add them back.
  transitTime: string;
  transitDaysMin: number;
  transitDaysMax: number;
}

interface CarrierSelectionProps {
  destinationZip: string;
  destinationAddress?: string;
  destinationCity?: string;
  destinationState?: string;
  destinationCompany?: string;
  destinationContact?: string;
  destinationPhone?: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  pallets: number;
  liftgate?: boolean;
  residential?: boolean;
  insideDelivery?: boolean;
  onSelect: (data: {
    method: 'shipping' | 'will_call';
    freightviewShipmentId?: string;
    selectedQuoteId?: string;
    selectedCarrierName?: string;
    selectedCarrierCode?: string;
    freightQuotes?: Quote[];
    freightTotalAmount?: number;
  }) => void;
  disabled?: boolean;
  // NEW: Accept quotes from parent (from Calculate Price)
  providedQuotes?: Quote[];
  providedShipmentId?: string;
  selectedQuoteId?: string;
}

export function CarrierSelection({
  onSelect,
  disabled = false,
  // Quotes from Calculate Price - this is the primary data source now
  providedQuotes,
  providedShipmentId,
  selectedQuoteId: initialSelectedQuoteId,
}: CarrierSelectionProps) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [allData, setAllData] = useState<any>(null);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Use provided quotes from Calculate Price if available
  useEffect(() => {
    if (providedQuotes?.length) {
      setQuotes(providedQuotes);
      setAllData({ quotes: providedQuotes, freightviewShipmentId: providedShipmentId });
      setSelected(initialSelectedQuoteId || providedQuotes[0].quoteId);
      setError('');
      setLoading(false);
    }
  }, [providedQuotes, providedShipmentId, initialSelectedQuoteId]);


  const selectQuote = (quoteId: string, data?: any) => {
    const d = data || allData;
    const q = d?.quotes?.find((x: Quote) => x.quoteId === quoteId);
    if (q) {
      setSelected(quoteId);
      onSelect({
        method: 'shipping',
        freightviewShipmentId: d.freightviewShipmentId,
        selectedQuoteId: quoteId,
        selectedCarrierName: q.carrierName,
        selectedCarrierCode: q.carrierCode,
        freightQuotes: d.quotes,
        freightTotalAmount: q.totalAmount,
      });
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-white">Select Carrier</h3>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <span className="ml-3 text-gray-400">Getting carrier quotes...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400 text-sm">{error}</p>
          <p className="mt-2 text-sm text-gray-400">
            Click "Calculate Price" to try again
          </p>
        </div>
      )}

      {!loading && !error && quotes.length > 0 && (
        <div className="space-y-2">
          <ShippingDisclaimer className="mb-3" />
          <p className="text-sm text-gray-400 mb-3">
            Top {quotes.length} carrier{quotes.length > 1 ? 's' : ''} by price - select one:
          </p>
          {quotes.map((q, i) => (
            <label
              key={q.quoteId}
              className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selected === q.quoteId
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-gray-600 hover:border-gray-500'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="carrier"
                  value={q.quoteId}
                  checked={selected === q.quoteId}
                  onChange={() => selectQuote(q.quoteId)}
                  disabled={disabled}
                  className="w-4 h-4 text-orange-500 border-gray-600 focus:ring-orange-500"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white">
                      {q.carrierName} - {q.serviceDescription}
                    </span>
                    {i === 0 && (
                      <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                        Lowest Price
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    {q.transitTime}
                  </div>
                </div>
              </div>
              <span className="text-xl font-bold text-white">
                ${q.totalAmount.toFixed(2)}
              </span>
            </label>
          ))}
        </div>
      )}

      {!loading && !error && quotes.length === 0 && (
        <div className="text-center py-6 text-gray-400 bg-keder-dark/30 rounded-lg border border-dashed border-gray-600">
          <svg className="w-8 h-8 mx-auto mb-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <p className="font-medium text-gray-300">Click "Calculate Price" to see carrier options</p>
          <p className="text-sm mt-1">Freight quotes will appear here after calculating your order price</p>
        </div>
      )}
    </div>
  );
}

export default CarrierSelection;
